// FARBLD: the far-building LOD draw (work item far-buildings-zoom). The bake's
// buildings_far.bin (pipeline/farbld.py) holds every building as one far
// shape per 2048 m chunk; client/src/farplan.js decides what is drawn (the
// wedge to the frame edge, minus every chunk run whose real GLB tile is
// drawn, under focus.tiers[t].far_buildings). Here: one mesh per chunk on
// the FOCUS layer only (the optic band, a raised VR optic, the armed desktop /
// phone view) — the naked-eye pass is unchanged.
//
// Geometry as stored: Int16 positions (x z from the chunk centre, y from the
// chunk's y_base, in units of q = 1/16 m) — the mesh's position / scale undo
// the quantisation, so the GPU holds 6 bytes a vertex; colours are linear
// u16 / col_scale per triangle, expanded per vertex as normalized Uint16 and
// scaled back by the material colour (= col_scale). MeshBasic, no lights: the
// bake folded the building's own baked light into the colour, like the GLB's
// COLOR_0. A chunk draws through an index of its visible runs (rewritten
// only when the drawn GLB set changes), so hidden runs keep it ONE draw.
// Warm-up (the TrailLines idiom, APP_SURFACE §Perf pass): with a tier budget
// the ONE material + a hidden keeper (the chunks' own attribute layout) are
// in the scene from the constructor, so every warm-up pass (canvas + optic
// target) compiles the far program before the file lands or the first zoom.
// FAR-SWAP (far-bld-model w41, APP_FACADES §11): the same file also draws in
// the MAIN view — per chunk a second mesh (layer 0) over the runs of the ring
// tiles the GLB draws now (BuildingManager.ringKeys), whose far shapes show
// only past their own switch distance (bldswap.js: aSwap per vertex, the
// complement of the GLB's collapse, SWAP_GLSL below). The file is fetched at
// boot (bulk) when the bake has it; the clusters (farClusters) go with each
// GLB tile's parse to the pool (buildings.js _load), which measures their e.
import * as THREE from 'three';
import { fetchAsset } from './assetq.js';
import { parseFar, farPlan, chunkRuns } from './farplan.js';
import { DRAW_ORDER } from './visframe.js';
import { swapArmOf, farClusters, writeFar, pixelAngle, SWAP_PX, TRIM_PX, FADE0, BAND_M } from './bldswap.js';

// the arm (?farbld=0|1|trim|swap; __vr.farbld(arm) at run time: the seam lab)
const ARM = swapArmOf(typeof location !== 'undefined' ? location.search : '');
export function setSwapArm(a) { Object.assign(ARM, swapArmOf('?farbld=' + a)); return { ...ARM }; }
export function swapArm() { return { ...ARM }; }
// ONE uniform for every program that swaps (the GLB facade programs, the far
// main program): x = 1 / (SWAP_PX · θ) (0: no swap), y = any rule on,
// z = 1 / (TRIM_PX · θ) (0: no trim drop), w = FADE0. Set per draw from the
// pass's own camera + viewport (swapBefore: the main view, each XR eye, the
// optic all get their own θ), so both models of a building decide alike.
export const SWAP_U = { value: new THREE.Vector4(0, 0, 0, FADE0) };
const _vp = new THREE.Vector4();
export function swapBefore(renderer, _scene, camera) {
  const cam = camera.isArrayCamera && camera.cameras && camera.cameras.length ? camera.cameras[0] : camera;
  renderer.getCurrentViewport(_vp);
  const th = pixelAngle(cam.projectionMatrix.elements, _vp.w);
  const v = SWAP_U.value;
  v.x = ARM.swap && th > 0 ? 1 / (SWAP_PX * th) : 0;
  v.z = ARM.trim && th > 0 ? 1 / (TRIM_PX * th) : 0;
  v.y = v.x > 0 || v.z > 0 ? 1 : 0;
}
// the far shape's half (after project_vertex): shown past S − BAND_M; inside
// (S − BAND_M, S + 2 m) pushed back 3 depth steps (≤ 0.5 % of its distance)
// so the GLB, still drawn there, wins the tie
const FAR_SWAP_GLSL = `
  {
    float sS = aSwap.z * 0.01 * uSwap.x + aSwap.w;
    float sD = distance(cameraPosition.xz, aSwap.xy);
    if (uSwap.x <= 0.0 || aSwap.z <= 0.0 || sD <= sS - ${BAND_M.toFixed(2)}) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    } else if (sD < sS + 2.0 && projectionMatrix[2][3] < -0.5) {
      float fDl = length(mvPosition.xyz), fDz = max(-mvPosition.z, 1e-3);
      float fNear = projectionMatrix[3][2] / (projectionMatrix[2][2] - 1.0);
      float fS = min(3.0 * fDz * fDl / (fNear * 16777216.0), 0.005 * fDl);
      mvPosition.xyz *= 1.0 + fS / fDl;
      gl_Position = projectionMatrix * mvPosition;
    }
  }`;
function farSwapPatch(mat) {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uSwap = SWAP_U;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 aSwap;\nuniform vec4 uSwap;')
      .replace('#include <project_vertex>', '#include <project_vertex>' + FAR_SWAP_GLSL);
  };
  mat.customProgramCacheKey = () => 'farbld-swap';
  mat.onBeforeRender = swapBefore;
}

export class FarBuildings {
  // meta.buildings_far ({ file, … } — absent: the bake has no far LOD and this
  // stays inert); tiers: renderconfig focus.tiers; tier: detailTier()
  constructor(scene, meta, baseUrl, tiers, tier) {
    this.scene = scene;
    this.info = (meta && meta.buildings_far) || null;
    this.baseUrl = baseUrl;
    this.cfg = (this.info && tiers && tiers[tier] && tiers[tier].far_buildings) || null;
    this.sw = null;               // farClusters: per tile the clusters, the far aSwap
    this.main = new Map();        // 'cx_cz' -> the main-view mesh (layer 0)
    this._mainGen = -1;           // buildings.drawnGen of the last main plan
    this.geos = new Map();        // 'cx_cz' -> the chunk geometry (both meshes share its attributes)
    this.group = new THREE.Group();
    this.group.name = 'buildings_far';
    scene.add(this.group);
    // FAR-SWAP: the main view's meshes + their keeper, a group of their own
    // (the focus group above holds only the focus material's meshes)
    this.groupMain = new THREE.Group();
    this.groupMain.name = 'buildings_far-main';
    scene.add(this.groupMain);
    this._failed = false;
    this.far = null;              // parsed FBL1 (farplan parseFar)
    this._loading = false;
    this.meshes = new Map();      // 'cx_cz' -> mesh (built on first draw, kept)
    this._sig = '';               // the last plan's signature
    this._gen = -1;               // focus.gen / buildings.drawnGen of the last plan
    this._drawnGen = -1;
    this.plan = null;             // the last farPlan (stats for the HUD / tests)
    this.mat = null;
    this.keeper = null;
    this.matMain = null;
    this.keeperMain = null;
    if (!this.info) return;
    const kg = new THREE.BufferGeometry();
    kg.setAttribute('position', new THREE.BufferAttribute(new Int16Array(9), 3));
    kg.setAttribute('color', new THREE.BufferAttribute(new Uint16Array(9), 3, true));
    kg.setAttribute('aSwap', new THREE.BufferAttribute(new Int16Array(12), 4));
    const keep = (mat, name, grp) => {
      const k = new THREE.Mesh(kg, mat);
      k.name = name;
      k.visible = false;
      k.frustumCulled = false;
      grp.add(k);
      return k;
    };
    // the main view's far material (FAR-SWAP) + its keeper: from boot, so
    // warm-up compiles the program before the first switch
    this.matMain = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true });
    farSwapPatch(this.matMain);
    this.keeperMain = keep(this.matMain, 'buildings_far-keeper-main', this.groupMain);
    this._load();                 // bulk, at boot: the main view needs it before any GLB tile
    if (!this.cfg) return;
    this.mat = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true });
    // the keeper: never drawn, never removed (update keeps it in the group)
    this.keeper = keep(this.mat, 'buildings_far-keeper', this.group);
  }

  // true while the far file is still coming (buildings.js holds its GLB
  // parses meanwhile: a tile parsed without its far tile never swaps)
  swapPending() { return !!this.info && !this.sw && !this._failed && (ARM.swap || ARM.trim); }

  // the far tile of GLB tile `key` for the pool (null: none / not loaded yet)
  swapTile(key) { return (this.sw && this.sw.tiles.get(key)) || null; }

  // the pool measured tile `key` (buildings.js): its clusters into the far
  // aSwap, the chunk's attribute re-uploaded
  applyTile(key, cl, pc) {
    const t = this.swapTile(key);
    if (!t || !cl || !pc || pc.length !== t.parts.length) return;
    t.cl = cl;
    t.pc = pc;
    writeFar(this.sw.aSwap, t);
    for (const c of this.far.chunks) {
      if (t.tri0 < c.tri0 || t.tri0 >= c.tri0 + c.ntris) continue;
      const g = this.geos.get(c.cx + '_' + c.cz);
      if (g) g.getAttribute('aSwap').needsUpdate = true;
    }
  }

  // fetched at boot
  _load() {
    if (this._loading || this.far || !this.info) return;
    this._loading = true;
    // `near` (FAR-SWAP): the GLB tiles' parses wait for it (swapPending)
    fetchAsset(`${this.baseUrl}/${this.info.file}`, { prio: 'near', as: 'arraybuffer' })
      .then((buf) => {
        if (!buf) throw new Error('HTTP error');
        this.far = parseFar(buf);
        this.sw = farClusters(this.far);
        if (this.mat) this.mat.color.setScalar(this.far.colScale);   // a uniform: same program
        this.matMain.color.setScalar(this.far.colScale);
        this._gen = -1;
        this._mainGen = -1;
      })
      .catch((e) => { this._failed = true; console.warn('[farbld] buildings_far unavailable:', e.message); });
  }

  // the chunk's geometry: position, colour, aSwap (shared by its focus and main meshes)
  _geo(c) {
    const k = c.cx + '_' + c.cz;
    let geo = this.geos.get(k);
    if (geo) return geo;
    const F = this.far;
    const pos = F.pos.subarray(c.tri0 * 9, (c.tri0 + c.ntris) * 9);
    const tc = F.col.subarray(c.tri0 * 3, (c.tri0 + c.ntris) * 3);
    const col = new Uint16Array(c.ntris * 9);
    for (let t = 0; t < c.ntris; t++) {
      const r = tc[t * 3], g = tc[t * 3 + 1], b = tc[t * 3 + 2];
      for (let v = 0; v < 3; v++) { const o = t * 9 + v * 3; col[o] = r; col[o + 1] = g; col[o + 2] = b; }
    }
    geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3, true));
    geo.setAttribute('aSwap', new THREE.BufferAttribute(this.sw.aSwap.subarray(c.tri0 * 12, (c.tri0 + c.ntris) * 12), 4));
    geo.computeBoundingSphere();
    this.geos.set(k, geo);
    return geo;
  }

  _mesh(c, layer, main = false) {
    const k = c.cx + '_' + c.cz, map = main ? this.main : this.meshes;
    let m = map.get(k);
    if (m) return m;
    const F = this.far, g0 = this._geo(c);
    // each mesh its own index / draw range over the shared attributes
    const geo = new THREE.BufferGeometry();
    for (const a in g0.attributes) geo.setAttribute(a, g0.attributes[a]);
    geo.boundingSphere = g0.boundingSphere;
    m = new THREE.Mesh(geo, main ? this.matMain : this.mat);
    m.position.set((c.cx + 0.5) * F.chunkM, c.yBase, (c.cz + 0.5) * F.chunkM);
    m.scale.setScalar(F.q);
    m.layers.set(layer);
    m.matrixAutoUpdate = false;
    m.updateMatrix();
    m.userData.runs = null;
    if (main) m.renderOrder = DRAW_ORDER.buildings;
    map.set(k, m);
    return m;
  }

  // the visible runs as the geometry's draw: ONE index per chunk, allocated
  // once (replacing an index attribute would leave the old GL buffer
  // behind), rewritten over the runs, drawn through its draw range
  _setRuns(m, c, runs) {
    const key = runs.map((r) => r.join(':')).join(',');
    if (m.userData.runs === key) return;
    m.userData.runs = key;
    const geo = m.geometry;
    if (!geo.index) geo.setIndex(new THREE.BufferAttribute(new Uint32Array(c.ntris * 3), 1));
    const idx = geo.index.array;
    let o = 0;
    for (const [t0, k] of runs) for (let i = t0 * 3, e = (t0 + k) * 3; i < e; i++) idx[o++] = i;
    geo.index.needsUpdate = true;
    geo.setDrawRange(0, o);
  }

  // every frame after buildings.update (focus = the Focus, buildings = the
  // BuildingManager); re-plans only when the wedge or the drawn GLB set moved
  update(focus, buildings) {
    if (!this.far) return;
    if (!this.sw) this.sw = farClusters(this.far);
    this._updateMain(buildings);
    if (!this.cfg) return;
    const fg = focus && focus.active ? focus.gen : -1;
    const dg = buildings ? buildings.drawnGen : 0;
    if (fg === this._gen && dg === this._drawnGen) return;
    this._gen = fg;
    this._drawnGen = dg;
    const plan = farPlan(this.far.chunks, focus, buildings ? buildings.drawnKeys() : null, this.cfg);
    this.plan = plan;
    const on = new Set([this.keeper]);
    for (const e of plan.list) {
      const m = this._mesh(e.chunk, focus.layer);
      this._setRuns(m, e.chunk, e.runs);
      if (!m.parent) this.group.add(m);
      on.add(m);
    }
    // a chunk out of the plan leaves the scene (its geometry stays built:
    // ≤ 23 chunks, 36 B a triangle)
    for (const m of [...this.group.children]) if (!on.has(m)) this.group.remove(m);
  }

  // FAR-SWAP: the main mesh of every chunk over the runs of the ring tiles
  // the GLB draws now (layer 0, visible), re-planned when that set moves; the
  // shader picks per building which of the two shows
  _updateMain(buildings) {
    const dg = buildings ? buildings.drawnGen : 0;
    if (dg === this._mainGen) return;
    this._mainGen = dg;
    const ring = buildings ? buildings.ringKeys() : new Set();
    for (const c of this.far.chunks) {
      const k = c.cx + '_' + c.cz;
      let any = false;
      for (const s of c.subs) if (ring.has(s.key)) { any = true; break; }
      const have = this.main.get(k);
      if (!any) { if (have && have.parent) this.groupMain.remove(have); continue; }
      const sub = { subs: c.subs.filter((s) => ring.has(s.key)) };
      const r = chunkRuns(sub, null);
      const m = this._mesh(c, 0, true);
      this._setRuns(m, c, r.runs);
      if (!m.parent) this.groupMain.add(m);
    }
  }
}
